import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import toast from 'react-hot-toast';
import {
  Globe, Upload, Phone, Mail, MapPin, Loader2,
  Image as ImageIcon, X as XIcon, Plus, Share2, AtSign, Hash, Copyright,
} from 'lucide-react';
import { getConfig } from '../../config';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import { uploadFile, uploadErrorMessage } from '../../services/imageUploadService';
import { resolveMediaUrl } from '../../utils/helpers';

export const sanitizeSlugInput = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-{2,}/g, '-');

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

export const EMPTY_ACADEMY_FORM = {
  name: '', slug: '', description: '',
  offerings: [], highlightsHeading: '', highlights: [],
  sportTypeIds: [],
  weekdayHours: { open: '06:00', close: '22:00' },
  saturdayHours: { open: '08:00', close: '20:00' },
  saturdayClosed: false,
  sundayHours: { open: '08:00', close: '20:00' },
  sundayClosed: false,
  address: '', phone: '', email: '', website: '',
  facebookLink: '', twitterLink: '', instagramLink: '', copyrightText: '',
  logo: '', backgroundImage: '',
};

// Maps a saved academy record (API shape) into the flat form shape above.
// Shared so create, legacy edit, and settings all interpret the same
// backend fields — including the saturday/sunday-used-to-be-one-"weekend"
// -block backward compat — identically.
export function academyToFormData(a) {
  const hours = a?.operatingHours || {};
  const satSource = hours.saturday || hours.weekend;
  const sunSource = hours.sunday || hours.weekend;
  return {
    name: a?.name || '',
    slug: a?.slug || '',
    description: stripHtml(a?.description),
    offerings: Array.isArray(a?.offerings) ? a.offerings : [],
    highlightsHeading: a?.highlightsHeading || '',
    highlights: Array.isArray(a?.highlights) ? a.highlights : [],
    sportTypeIds: (a?.sportTypes || []).map(s => s.id),
    weekdayHours: hours.weekday || { open: '06:00', close: '22:00' },
    saturdayHours: satSource?.closed ? { open: '08:00', close: '20:00' } : (satSource || { open: '08:00', close: '20:00' }),
    saturdayClosed: satSource?.closed === true,
    sundayHours: sunSource?.closed ? { open: '08:00', close: '20:00' } : (sunSource || { open: '08:00', close: '20:00' }),
    sundayClosed: sunSource?.closed === true,
    address: a?.address || '',
    phone: a?.phone || '',
    email: a?.email || '',
    website: a?.website || '',
    facebookLink: a?.facebookLink || '',
    twitterLink: a?.twitterLink || '',
    instagramLink: a?.instagramLink || '',
    copyrightText: a?.copyrightText || '',
    logo: a?.logo || '',
    backgroundImage: a?.backgroundImage || '',
  };
}

// Builds the exact save/create payload from form state — the single source
// of truth for how form fields map to API fields, so create and edit can
// never drift on this mapping again.
export function buildAcademyPayload(formData, selectedAddressDetails) {
  return {
    name: formData.name.trim(),
    slug: formData.slug.trim().replace(/^-+|-+$/g, ''),
    description: formData.description?.trim() || null,
    offerings: formData.offerings,
    highlightsHeading: formData.highlightsHeading?.trim() || null,
    highlights: formData.highlights,
    address: formData.address?.trim() || null,
    ...(selectedAddressDetails ? {
      addressPlaceId: selectedAddressDetails.placeId,
      addressLat: selectedAddressDetails.coordinates?.lat ?? null,
      addressLng: selectedAddressDetails.coordinates?.lng ?? null,
      addressCity: selectedAddressDetails.city || null,
      addressState: selectedAddressDetails.state || null,
      addressCountry: selectedAddressDetails.country || null,
      addressZipCode: selectedAddressDetails.zipCode || null,
      addressStreetNumber: selectedAddressDetails.streetNumber || null,
      addressStreetName: selectedAddressDetails.streetName || null,
      addressLandmark: selectedAddressDetails.landmark || null,
    } : {}),
    phone: formData.phone?.trim() || null,
    email: formData.email?.trim() || null,
    website: formData.website?.trim() || null,
    facebookLink: formData.facebookLink?.trim() || null,
    twitterLink: formData.twitterLink?.trim() || null,
    instagramLink: formData.instagramLink?.trim() || null,
    copyrightText: formData.copyrightText?.trim() || null,
    logo: formData.logo || null,
    backgroundImage: formData.backgroundImage || null,
    sportTypeIds: formData.sportTypeIds,
    operatingHours: {
      weekday: formData.weekdayHours,
      saturday: formData.saturdayClosed ? { closed: true } : formData.saturdayHours,
      sunday: formData.sundayClosed ? { closed: true } : formData.sundayHours,
    },
  };
}

// Returns an error message string if the form isn't ready to submit, else null.
export function validateAcademyForm(formData) {
  if (!formData.name.trim()) return 'Academy name is required.';
  const slug = formData.slug.trim().replace(/^-+|-+$/g, '');
  if (!slug) return 'URL slug is required.';
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
    return 'URL slug can only contain lowercase letters, numbers, and hyphens.';
  }
  if (formData.sportTypeIds.length === 0) return 'Please select at least one sport.';
  if (!formData.address.trim()) return 'Please enter an address.';
  return null;
}

// Folds in whatever AcademyBasicInfoForm.getPendingAdditions() returned (typed
// into the offering/highlight inputs but never explicitly "+ Add"ed) so a
// parent's submit handler can call this right before buildAcademyPayload
// instead of silently dropping that text. Safe to call with pending=null/undefined.
export function mergePendingAdditions(formData, pending) {
  if (!pending) return formData;
  let offerings = formData.offerings;
  if (pending.offering && !offerings.includes(pending.offering) && offerings.length < 20) {
    offerings = [...offerings, pending.offering];
  }
  let highlights = formData.highlights;
  if (pending.highlight && highlights.length < 12) {
    highlights = [...highlights, pending.highlight];
  }
  return { ...formData, offerings, highlights };
}

function SectionCard({ title, children, theme }) {
  return (
    <div className={`${theme.card} p-6 rounded-xl`}>
      <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>{title}</h3>
      {children}
    </div>
  );
}

/**
 * The single implementation of every "basic info" field an academy has —
 * used by CreateEditAcademyPage (create + legacy edit route) and
 * AcademySettingsTab's Basic section. Two components duplicating this same
 * form independently is exactly how the Sunday-hours and address-autocomplete
 * gaps happened — features added to one silently never reached the other.
 *
 * Controlled from the outside: the parent owns `formData` and decides how/
 * when to persist it (single create/update call vs. a dedicated Save
 * button) — this component only renders fields and reports changes up.
 */
const AcademyBasicInfoForm = forwardRef(function AcademyBasicInfoForm({
  formData,
  onChange,
  onHoursChange,
  onAddressSelect,
  sportTypes,
  theme,
  showSlugWarning = false,
  slugExtra = null,
}, ref) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [offeringInput, setOfferingInput] = useState('');
  const [highlightTitleInput, setHighlightTitleInput] = useState('');
  const [highlightDescInput, setHighlightDescInput] = useState('');
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);

  const addOffering = () => {
    const value = offeringInput.trim();
    if (!value) return;
    if (formData.offerings.includes(value)) { setOfferingInput(''); return; }
    if (formData.offerings.length >= 20) { toast.error('Up to 20 offerings.'); return; }
    onChange('offerings', [...formData.offerings, value]);
    setOfferingInput('');
  };
  const removeOffering = (index) => onChange('offerings', formData.offerings.filter((_, i) => i !== index));

  const addHighlight = () => {
    const title = highlightTitleInput.trim();
    const description = highlightDescInput.trim();
    if (!title || !description) { toast.error('Both a title and description are needed.'); return; }
    if (formData.highlights.length >= 12) { toast.error('Up to 12 highlights.'); return; }
    onChange('highlights', [...formData.highlights, { title, description }]);
    setHighlightTitleInput('');
    setHighlightDescInput('');
  };
  const removeHighlight = (index) => onChange('highlights', formData.highlights.filter((_, i) => i !== index));

  // Typing something into the "What You Offer" / Highlights inputs and
  // clicking Save/Create without ever pressing the separate "+ Add" button
  // silently discarded that text — it only ever entered formData via addOffering/
  // addHighlight. Parents call this right before building the save payload so
  // anything still sitting in these buffers gets committed instead of lost.
  // A half-filled highlight (only title or only description) can't be validly
  // auto-committed — that one still requires an explicit Add.
  // Returns typed-but-never-"+ Add"ed text directly (rather than committing via
  // onChange and expecting the parent to read updated formData afterward — that
  // doesn't work, onChange -> setState is async, so a synchronous submit handler
  // would still see the pre-commit formData). The parent merges these into the
  // formData snapshot it already has right before building the save payload,
  // so nothing typed gets silently dropped just because "+ Add" was never clicked.
  useImperativeHandle(ref, () => ({
    getPendingAdditions: () => ({
      offering: offeringInput.trim() || null,
      highlight: (highlightTitleInput.trim() && highlightDescInput.trim())
        ? { title: highlightTitleInput.trim(), description: highlightDescInput.trim() }
        : null,
    }),
    // Called after a successful save that merged in getPendingAdditions()'s
    // result, so the now-persisted text doesn't linger in the input boxes
    // looking unsaved.
    clearPendingInputs: () => {
      setOfferingInput('');
      setHighlightTitleInput('');
      setHighlightDescInput('');
    },
  }));

  const toggleSportType = (id) =>
    onChange('sportTypeIds', formData.sportTypeIds.includes(id)
      ? formData.sportTypeIds.filter(s => s !== id)
      : [...formData.sportTypeIds, id]);

  const handleLogoUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Please upload a valid image file.'); return; }
    setUploadingLogo(true);
    try {
      const { url } = await uploadFile(file, 'group_assets');
      onChange('logo', url);
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBackgroundUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Please upload a valid image file.'); return; }
    setUploadingBackground(true);
    try {
      const { url } = await uploadFile(file, 'group_assets');
      onChange('backgroundImage', url);
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoUpload(file);
  };

  return (
    <>
      <SectionCard title="Hero Image" theme={theme}>
        <p className={`text-sm ${theme.textMuted} mb-4`}>
          This image appears as the full-width banner at the top of your academy page.
        </p>
        <div
          className="w-full h-36 rounded-xl mb-4 flex items-end overflow-hidden relative"
          style={{
            backgroundImage: formData.backgroundImage
              ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${resolveMediaUrl(formData.backgroundImage)})`
              : 'linear-gradient(135deg, #1e293b 0%, #1e40af 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="p-4">
            <p className="text-white font-bold text-base leading-tight">{formData.name || 'Your Academy Name'}</p>
            <p className="text-white/70 text-xs mt-0.5">Academy · Sports</p>
          </div>
          {!formData.backgroundImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white/40">
                <ImageIcon size={28} className="mx-auto mb-1" />
                <p className="text-xs">No hero image set</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer text-sm font-medium transition ${
            uploadingBackground ? 'opacity-50 pointer-events-none' : `${theme.border} ${theme.text} hover:bg-gray-50 dark:hover:bg-gray-800`
          }`}>
            {uploadingBackground ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploadingBackground ? 'Uploading…' : 'Upload from device'}
            <input
              ref={bgInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { handleBackgroundUpload(e.target.files[0]); e.target.value = ''; }}
              disabled={uploadingBackground}
            />
          </label>
          <span className={`text-xs ${theme.textMuted}`}>JPG, PNG or WebP · Recommended 1600×600</span>
          {formData.backgroundImage && (
            <button
              type="button"
              onClick={() => onChange('backgroundImage', '')}
              className={`ml-auto p-2 border ${theme.border} rounded-lg ${theme.cardHover || ''} transition`}
              title="Remove hero image"
            >
              <XIcon size={15} className="text-red-400" />
            </button>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Branding & Identity" theme={theme}>
        <div className="mb-6">
          <label className={`block text-sm font-semibold ${theme.text} mb-2`}>Academy Logo</label>
          <div className="flex items-start gap-4">
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-lg overflow-hidden"
              style={formData.logo
                ? { backgroundImage: `url(${resolveMediaUrl(formData.logo)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { backgroundColor: '#e5e7eb', color: '#6b7280' }
              }
            >
              {!formData.logo && (formData.name || 'A').charAt(0).toUpperCase()}
            </div>
            <div
              className={`flex-1 border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${isDragging ? 'border-blue-400 bg-blue-50' : `${theme.border}`} ${uploadingLogo ? 'opacity-50 pointer-events-none' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleLogoDrop}
              onClick={() => logoInputRef.current?.click()}
            >
              {uploadingLogo
                ? <Loader2 size={20} className={`mx-auto mb-1 animate-spin ${theme.textMuted}`} />
                : <Upload size={20} className={`mx-auto mb-1 ${theme.textMuted}`} />
              }
              <p className={`text-sm font-medium ${theme.textSecondary}`}>
                {uploadingLogo ? 'Uploading…' : <>Drop your logo here or <span className="text-blue-600">browse</span></>}
              </p>
              <p className={`text-xs ${theme.textMuted} mt-0.5`}>PNG, JPG or SVG (max 2MB)</p>
            </div>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { handleLogoUpload(e.target.files[0]); e.target.value = ''; }} disabled={uploadingLogo} />
          </div>
          {formData.logo && (
            <button type="button" onClick={() => onChange('logo', '')} className="mt-2 text-xs text-red-500 hover:underline">Remove logo</button>
          )}
        </div>

        <div className="mb-4">
          <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}>Academy Name <span className="text-red-500">*</span></label>
          <input type="text" value={formData.name} onChange={e => onChange('name', e.target.value)}
            className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="e.g. Elite Tennis Academy" />
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className={`text-sm font-semibold ${theme.text}`}>URL Slug <span className="text-red-500">*</span></label>
            {slugExtra}
          </div>
          <input type="text" value={formData.slug} onChange={e => onChange('slug', sanitizeSlugInput(e.target.value))}
            className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="e.g. elite-tennis-academy" />
          <p className={`flex items-center gap-1.5 text-xs ${theme.textMuted} mt-1.5 truncate`}>
            <Globe size={12} className="flex-shrink-0" />
            {window.location.origin}/academy/{formData.slug || '…'}
          </p>
          {showSlugWarning && (
            <p className="text-xs text-amber-500 mt-1">
              Changing this breaks any links to your current public page already shared out.
            </p>
          )}
        </div>

        <div>
          <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}>About Your Academy</label>
          <textarea value={formData.description} onChange={e => onChange('description', e.target.value)} rows={5}
            className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm resize-none`}
            placeholder="Describe your academy, mission, and what makes you unique..." />
        </div>

        <div className="mt-4">
          <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}>What You Offer</label>
          <p className={`text-xs ${theme.textMuted} mb-2`}>
            Short bullets shown on your Overview page — e.g. "Private Coaching", "Junior Development". Not tied to your Programs list; those are managed separately.
          </p>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={offeringInput}
              onChange={e => setOfferingInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOffering(); } }}
              maxLength={100}
              className={`flex-1 px-4 py-2.5 rounded-lg border ${theme.input} text-sm`}
              placeholder="e.g. Beginner Fundamentals"
            />
            <button type="button" onClick={addOffering}
              className="px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center gap-1.5 shrink-0">
              <Plus size={16} /> Add
            </button>
          </div>
          {formData.offerings.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.offerings.map((offering, i) => (
                <span key={`${offering}-${i}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium">
                  {offering}
                  <button type="button" onClick={() => removeOffering(i)} className="hover:text-blue-900 dark:hover:text-blue-100">
                    <XIcon size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}>Highlights Section</label>
          <p className={`text-xs ${theme.textMuted} mb-2`}>
            The "Why Train With Us" style section on your Overview page. Set your own heading (e.g. "Why Train With Us", "Why Join Us") and a few highlights, each with a short title and description.
          </p>
          <input
            type="text"
            value={formData.highlightsHeading}
            onChange={e => onChange('highlightsHeading', e.target.value)}
            maxLength={100}
            className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm mb-3`}
            placeholder='Section heading — e.g. "Why Train With Us"'
          />
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              value={highlightTitleInput}
              onChange={e => setHighlightTitleInput(e.target.value)}
              maxLength={100}
              className={`flex-1 px-4 py-2.5 rounded-lg border ${theme.input} text-sm`}
              placeholder="Title — e.g. Certified Coaches"
            />
            <input
              type="text"
              value={highlightDescInput}
              onChange={e => setHighlightDescInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addHighlight(); } }}
              maxLength={300}
              className={`flex-[2] px-4 py-2.5 rounded-lg border ${theme.input} text-sm`}
              placeholder="Description — e.g. All coaches hold USPTA / PTR certifications"
            />
            <button type="button" onClick={addHighlight}
              className="px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center justify-center gap-1.5 shrink-0">
              <Plus size={16} /> Add
            </button>
          </div>
          {formData.highlights.length > 0 && (
            <div className="space-y-2">
              {formData.highlights.map((h, i) => (
                <div key={`${h.title}-${i}`}
                  className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${theme.border}`}>
                  <div>
                    <p className={`text-sm font-semibold ${theme.text}`}>{h.title}</p>
                    <p className={`text-xs ${theme.textMuted}`}>{h.description}</p>
                  </div>
                  <button type="button" onClick={() => removeHighlight(i)} className="text-red-400 hover:text-red-600 shrink-0">
                    <XIcon size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Sports Supported" theme={theme}>
        <p className={`text-sm ${theme.textMuted} mb-4`}>Select all sports your academy offers programs for.</p>
        {sportTypes.length === 0 ? (
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin text-gray-400" />
            <span className={`text-sm ${theme.textMuted}`}>Loading sports…</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sportTypes.map(sport => (
              <button
                key={sport.id}
                type="button"
                onClick={() => toggleSportType(sport.id)}
                className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors text-left ${
                  formData.sportTypeIds.includes(sport.id)
                    ? 'bg-blue-500 text-white border-blue-500'
                    : `${theme.border} ${theme.text} hover:bg-gray-100 dark:hover:bg-gray-800`
                }`}
              >
                {sport.sportName}
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Operating Hours" theme={theme}>
        <div className="mb-5">
          <label className={`block text-sm font-semibold ${theme.text} mb-2`}>Weekdays (Mon – Fri)</label>
          <div className="grid grid-cols-2 gap-4">
            {['open', 'close'].map(field => (
              <div key={field}>
                <label className={`block text-xs ${theme.textMuted} mb-1`}>{field === 'open' ? 'Open' : 'Close'}</label>
                <input
                  type="time"
                  value={formData.weekdayHours[field]}
                  onChange={e => onHoursChange('weekdayHours', field, e.target.value)}
                  className={`w-full px-4 py-2 border ${theme.border} rounded-lg ${theme.input} text-sm`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Saturday & Sunday — split apart (rather than one combined "weekend"
            block) since academies commonly run one of the two days but not
            the other, e.g. open Saturday clinics but closed Sundays. */}
        {[
          { key: 'saturday', label: 'Saturday' },
          { key: 'sunday', label: 'Sunday' },
        ].map(({ key, label }) => {
          const hoursKey = `${key}Hours`;
          const closedKey = `${key}Closed`;
          const closed = formData[closedKey];
          return (
            <div key={key} className="mb-5 last:mb-0">
              <div className="flex items-center justify-between mb-2">
                <label className={`text-sm font-semibold ${theme.text}`}>{label}</label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className={`text-xs font-medium ${closed ? 'text-red-500' : theme.textMuted}`}>
                    {closed ? 'Closed' : 'Open'}
                  </span>
                  <div
                    onClick={() => onChange(closedKey, !closed)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${closed ? 'bg-red-400' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 bg-white rounded-full shadow transform transition-transform ${closed ? 'translate-x-4' : 'translate-x-1'}`} />
                  </div>
                </label>
              </div>
              {closed ? (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border ${theme.border} ${theme.background}`}>
                  <span className="text-red-500 text-sm font-medium">Closed on {label}</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {['open', 'close'].map(field => (
                    <div key={field}>
                      <label className={`block text-xs ${theme.textMuted} mb-1`}>{field === 'open' ? 'Open' : 'Close'}</label>
                      <input
                        type="time"
                        value={formData[hoursKey][field]}
                        onChange={e => onHoursChange(hoursKey, field, e.target.value)}
                        className={`w-full px-4 py-2 border ${theme.border} rounded-lg ${theme.input} text-sm`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </SectionCard>

      <SectionCard title="Contact & Location" theme={theme}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><MapPin size={13} className="inline mr-1" />Address</label>
            <GooglePlacesAutocomplete
              googleApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
              placeholder="123 Sports Blvd, City, State"
              theme={theme}
              initialValue={formData.address}
              onAddressSelect={onAddressSelect}
            />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Phone size={13} className="inline mr-1" />Phone</label>
            <input type="text" value={formData.phone} onChange={e => onChange('phone', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Mail size={13} className="inline mr-1" />Email</label>
            <input type="email" value={formData.email} onChange={e => onChange('email', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="info@youracademy.com" />
          </div>
          <div className="md:col-span-2">
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Globe size={13} className="inline mr-1" />Website URL</label>
            <input type="url" value={formData.website} onChange={e => onChange('website', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="https://youracademy.com" />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Social Links & Footer" theme={theme}>
        <p className={`text-xs ${theme.textMuted} mb-4`}>
          Shown in the "Follow Us" section and footer of your public Overview page.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Share2 size={13} className="inline mr-1" />Facebook URL</label>
            <input type="url" value={formData.facebookLink} onChange={e => onChange('facebookLink', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="https://facebook.com/youracademy" />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><AtSign size={13} className="inline mr-1" />Twitter / X URL</label>
            <input type="url" value={formData.twitterLink} onChange={e => onChange('twitterLink', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="https://x.com/youracademy" />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Hash size={13} className="inline mr-1" />Instagram URL</label>
            <input type="url" value={formData.instagramLink} onChange={e => onChange('instagramLink', e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`} placeholder="https://instagram.com/youracademy" />
          </div>
          <div>
            <label className={`block text-sm font-semibold ${theme.text} mb-1.5`}><Copyright size={13} className="inline mr-1" />Copyright Statement</label>
            <input type="text" value={formData.copyrightText} onChange={e => onChange('copyrightText', e.target.value)}
              maxLength={300}
              className={`w-full px-4 py-2.5 rounded-lg border ${theme.input} text-sm`}
              placeholder={`© ${new Date().getFullYear()} ${formData.name || 'Your Academy'}. All rights reserved.`} />
            <p className={`text-xs ${theme.textMuted} mt-1`}>Leave blank to use the default shown above.</p>
          </div>
        </div>
      </SectionCard>
    </>
  );
});

export default AcademyBasicInfoForm;
